/**
 * createContactApiClient — canonical axios client factory for marketing
 * contact-form POSTs (MARKETING-CONTACT-CLIENT-001).
 *
 * Promoted in Wave 6.1 of GEN0-SWEEP-001 from 7 identical per-app
 * `apps/<app>-web/src/portals/marketing-website/services/contactApiClient.js`
 * duplicates. Each consumer app now constructs its own client inline with
 * its app-specific endpoint string:
 *
 *   import { createContactApiClient } from '@puffin-core/marketing-shell';
 *   const contactApi = createContactApiClient({ endpoint: '/api/v1/marketing/contact' });
 *
 * The factory is endpoint-agnostic, axios-DI-friendly and AbortSignal-aware.
 *
 * ⚠️ This paragraph asserted the OPPOSITE of the code until 2026-09-01: it advertised "a permissive
 * response unwrap (`response.data?.data ?? response.data ?? {}`)… the broader form is a superset",
 * which RES-CLIENT-001 deleted on 2026-08-26 precisely because that permissiveness ended in a
 * FALSE SUCCESS (see the comment at the read itself). A docblock that outlives its mechanism is
 * how the read came to mean two things in the first place.
 *
 * 🔴 This factory keeps its OWN `axios.create()` — owner ruling R16 on `Q-N51-MARKETING-CONTACT-FACADE`
 * (RES-CLIENT-001, 2026-09-01), a deliberate classification, not an oversight: it runs on nine
 * PRERENDERED marketing sites, pre-auth, where the app api-client's token storage and refresh
 * interceptors have no session to act on, and `marketingEntryBarrelFreedom` already caught one
 * regression of exactly that shape. What R16 DID convert is the envelope read: the transport is
 * wrapped in `createApiFacade` from the dependency-free `./facade` subpath (below). The package's
 * request-header concerns it composes by hand, as every owner-ruled transport does: the realtime-origin
 * stamp through the `./realtime-origin` subpath (APPROVALS-STORE-ROOMS-001 U10; the
 * `realtimeOriginOnEveryTransport` contract holds that line). This paragraph said "NOT converted to
 * `createApiFacade`" and "queued for the owner" for seven weeks after R16 had ruled and the facade
 * had landed — a docblock that outlives its ruling, the same defect the paragraph above records.
 *
 * The returned client exposes:
 *   - `submitContact(payload, {signal?})` — POSTs JSON to `endpoint` and
 *     returns the envelope's payload. Errors propagate verbatim so
 *     the consumer hook (`createUseContactForm`) can map err.response.status
 *     to user-visible i18n keys.
 *   - `endpoint` — the configured endpoint string, so consumers can pass
 *     it back to `<ContactForm>`'s `apiPath` slot without re-declaring.
 *
 * @module @puffin-core/marketing-shell/createContactApiClient
 */

import axios from 'axios';
// 🔴 RES-CLIENT-001 `Q-N51` (owner ruling R16, 2026-09-01): the DEPENDENCY-FREE `./facade`
// subpath, never the barrel. The barrel re-exports twelve modules, one of which imports axios,
// and `marketingEntryBarrelFreedom` exists because a barrel import into these nine prerendered
// marketing bundles has regressed before. `createApiFacade`'s transitive graph is 9 modules and
// ZERO external packages — measured by walking it, which is what R16 authorised.
import { createApiFacade } from '@puffin-core/api-client/facade';
// The realtime-origin SUBPATH, never the barrel (`marketingEntryBarrelFreedom`): the ONE stamp a write composes
// (APPROVALS-STORE-ROOMS-001 U10) is two small modules, not the app client's token storage and refresh chain.
import { realtimeOriginHeaders } from '@puffin-core/api-client/realtime-origin';

/**
 * @typedef {Object} ContactApiClient
 * @property {(payload: Object, opts?: {signal?: AbortSignal}) => Promise<Object>} submitContact
 *   POSTs `payload` to the configured endpoint with `Content-Type: application/json`
 *   and returns the unwrapped data envelope.
 * @property {string} endpoint  The configured endpoint string.
 */

/**
 * Build a contact-form API client.
 *
 * @param {Object} config
 * @param {string} config.endpoint  Required. The POST endpoint (e.g.
 *   '/api/v1/marketing/contact' or '/api/v1/purchase/contact').
 * @param {import('axios').AxiosInstance} [config.axiosInstance]  Optional
 *   pre-built axios instance (used in tests to inject a mock; production
 *   builds use the default axios.create()). It is wrapped in a FACADE before use — this
 *   factory keeps its own transport because the marketing sites are pre-auth and must not
 *   pull the app api-client's token storage and refresh interceptors.
 * @returns {ContactApiClient}
 * @throws {TypeError} when `endpoint` is missing, not a string, or empty.
 */
export default function createContactApiClient(config) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('createContactApiClient: config object with `endpoint` is required');
  }
  const { endpoint, axiosInstance } = config;
  if (typeof endpoint !== 'string' || endpoint.length === 0) {
    throw new TypeError('createContactApiClient: `endpoint` must be a non-empty string');
  }

  // The transport stays bespoke and pre-auth; only the ENVELOPE READING is delegated. A facade
  // needs nothing more than `request(config)`, which a bare axios instance has.
  const api = createApiFacade({ transport: axiosInstance || axios.create() });

  async function submitContact(payload, opts = {}) {
    const { signal } = opts;
    // APPROVALS-STORE-ROOMS-001 U10: this owner-ruled transport (R16) composes the package's ONE realtime-origin
    // stamp at the write, like every transport — a prerendered marketing site registers no realtime source, so
    // the helper answers nothing here today; the rule is uniform so the day one does, nothing moves.
    const data = await api.post(endpoint, payload, {
      headers: { 'Content-Type': 'application/json', ...realtimeOriginHeaders('post') },
      signal,
    });
    // 🔴 RES-CLIENT-001 (2026-08-26). Was `response?.data?.data ?? response?.data ?? {}` — three
    // contracts in one expression, ending in a FALSE SUCCESS. `buildContactOutcomeAdapter` reads
    // `data.submissionId || null` and returns `{success: true, outcome: 'success'}` regardless, so
    // a body this could not read told the visitor their message was sent, on all nine marketing
    // sites, with no submission id and nothing logged.
    //
    // Both arms measured dead before deletion, not assumed. MCR-001's handler answers with
    // `sendCreated` (an envelope). The idempotent-REPLAY path looked like the counter-example —
    // `createIdempotencyGuard` replays through `sendUnenvelopedJson` — but that helper means "do
    // not wrap AGAIN": the cached body is whatever `res.json` received, i.e. the original
    // envelope, and the guard's own comment says "its canonicality is whatever the original
    // response's was". So both paths put an envelope on the wire and the first arm covers both.
    //
    // 🔴 R16: the envelope hop is now the TRANSPORT's, not this file's. `api.post` resolves the
    // PAYLOAD, and an unreadable body raises `EnvelopeShapeError` instead of resolving `{}` — so
    // the false-success this comment describes is unreachable by construction, not by discipline.
    return data;
  }

  return { submitContact, endpoint };
}
